import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import Sidebar from "../components/Sidebar";
import { ChevronLeft, ChevronRight, CalendarDays, Clock, MapPin, AlertTriangle, Briefcase } from "lucide-react";
import { i18nLocale } from "../lib/locale";

const WEEKDAYS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const WEEKDAY_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

const startOfWeek = (date) => {
  const d = new Date(date);
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - dow);
  d.setHours(0, 0, 0, 0);
  return d;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const isoDate = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, "0"), day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const fmtRange = (from, to) =>
  `${from.toLocaleDateString(i18nLocale(), { day: "numeric", month: "short" })} – ${to.toLocaleDateString(i18nLocale(), { day: "numeric", month: "short", year: "numeric" })}`;

export default function CalendarWeek() {
  const [needs, setNeeds] = useState([]);
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));

  useEffect(() => {
    api.get("/needs").then(({ data }) => setNeeds(data || []));
  }, []);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(anchor, i)), [anchor]);

  // Map: dayIndex -> array of {need, slots}
  const grouped = useMemo(() => {
    const map = Array.from({ length: 7 }, () => []);
    for (const n of needs) {
      if (n.status === "draft" || n.status === "closed") continue;
      if (n.mode === "permanent") {
        (n.weekdays || []).forEach((wd) => {
          const i = WEEKDAY_KEYS.indexOf(wd);
          if (i >= 0) {
            const slots = n.per_day?.[wd]?.slots || n.global_slots || [];
            map[i].push({ need: n, slots });
          }
        });
      } else {
        (n.dates || []).forEach((iso) => {
          const idx = days.findIndex((d) => isoDate(d) === iso);
          if (idx >= 0) {
            const slots = n.per_day?.[iso]?.slots || n.global_slots || [];
            map[idx].push({ need: n, slots });
          }
        });
      }
    }
    return map;
  }, [needs, days]);

  return (
    <div className="flex bg-[#F7F8F6] min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0">
        <div className="max-w-7xl mx-auto p-6 lg:p-10">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="text-3xl sm:text-4xl font-medium tracking-tight" style={{ fontFamily: "Outfit" }}>Ma semaine</h1>
              <p className="text-sm text-[#6B726A] mt-1">Vue d'ensemble des besoins publiés et permanents.</p>
            </div>
            <div className="flex items-center gap-2 bg-white rounded-full border border-[#E2E5E0] px-2 py-1">
              <button onClick={() => setAnchor((d) => addDays(d, -7))} className="w-8 h-8 rounded-full hover:bg-[#F0F2EF] grid place-items-center" data-testid="week-prev">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="text-sm font-medium px-3" data-testid="week-range">{fmtRange(anchor, addDays(anchor, 6))}</div>
              <button onClick={() => setAnchor((d) => addDays(d, 7))} className="w-8 h-8 rounded-full hover:bg-[#F0F2EF] grid place-items-center" data-testid="week-next">
                <ChevronRight className="w-4 h-4" />
              </button>
              <button onClick={() => setAnchor(startOfWeek(new Date()))} className="text-xs text-[#1A4D2E] px-3 py-1 hover:underline" data-testid="week-today">Aujourd&apos;hui</button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-3" data-testid="week-grid">
            {days.map((d, i) => {
              const items = grouped[i];
              const isToday = isoDate(d) === isoDate(new Date());
              return (
                <div key={i} className={`bg-white rounded-2xl border ${isToday ? "border-[#1A4D2E]" : "border-[#E2E5E0]"} p-3 min-h-[180px]`}
                     data-testid={`day-col-${WEEKDAY_KEYS[i]}`}>
                  <div className={`text-[11px] uppercase tracking-wider font-semibold mb-1 ${isToday ? "text-[#1A4D2E]" : "text-[#6B726A]"}`}>
                    {WEEKDAYS[i]}
                  </div>
                  <div className={`text-lg font-medium mb-3 ${isToday ? "text-[#1A4D2E]" : "text-[#1f2421]"}`} style={{ fontFamily: "Outfit" }}>
                    {d.getDate()} <span className="text-xs text-[#6B726A] font-normal">{d.toLocaleDateString(i18nLocale(), { month: "short" })}</span>
                  </div>
                  {items.length === 0 ? (
                    <div className="text-xs text-[#9AA098] italic">—</div>
                  ) : (
                    <div className="space-y-2">
                      {items.map(({ need, slots }, idx) => (
                        <div key={`${need.id}-${idx}`} className="text-xs p-2 rounded-lg bg-[#E8F0EA] border border-[#1A4D2E]/15">
                          <div className="flex items-center gap-1 text-[#1A4D2E] font-medium">
                            <Briefcase className="w-3 h-3" /> {need.role_type || "Candidat"}
                            {need.urgent && <AlertTriangle className="w-3 h-3 text-[#E3655B] ml-auto" />}
                          </div>
                          {slots.slice(0, 3).map((s, j) => (
                            <div key={j} className="flex items-center gap-1 text-[#6B726A] mt-0.5">
                              <Clock className="w-3 h-3" /> {s.start}–{s.end}
                            </div>
                          ))}
                          {need.address && (
                            <div className="flex items-center gap-1 text-[#6B726A] mt-0.5 truncate">
                              <MapPin className="w-3 h-3 shrink-0" /> <span className="truncate">{need.address.split(",")[0]}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {needs.length === 0 && (
            <div className="bg-white rounded-2xl border border-[#E2E5E0] p-10 text-center mt-6">
              <CalendarDays className="w-8 h-8 text-[#6B726A] mx-auto mb-2" />
              <p className="text-sm text-[#6B726A]">Aucun besoin pour cette semaine.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
